import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
  Box, Table, Thead, Tbody, Tr, Th, Td, Flex, Text, Badge, Spinner,
  Button, Heading, Container
} from "@chakra-ui/react";
import { AppContext } from './AppContext';
import { callApi } from '../callApi';
import HeaderMenus from './HeaderMenus';

// Helper to format currency
const formatCurrency = (value) => {
  if (value === null || value === undefined) return 'N/A';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
};

// Helper for score color
const getScoreColor = (score) => {
  if (!score) return 'gray';
  if (score >= 8) return 'green';
  if (score >= 5) return 'yellow';
  return 'red';
};

export default function CaseImpactPortfolio() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'impact_score', direction: 'desc' });
  const { accessToken } = useContext(AppContext);

  useEffect(() => {
    fetchPortfolio();
  }, []);

  const fetchPortfolio = async () => {
    setLoading(true);
    try {
      const response = await callApi('/api/impact-analysis/portfolio', accessToken);
      const result = await response.json();
      if (result.status === 'success') {
        setData(result.portfolio || []);
      }
    } catch (error) {
      console.error('Error fetching portfolio:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedData = useMemo(() => {
    let sortableItems = [...data];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        
        // Handle nested analysis_data fields for sorting
        if (sortConfig.key === 'variance_category') {
          aValue = a.analysis_data?.variance_category || '';
          bValue = b.analysis_data?.variance_category || '';
        }

        if (aValue === null) return 1;
        if (bValue === null) return -1;
        
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [data, sortConfig]);

  const renderCodeList = (codes, title) => (
    <Box mb={2}>
      <Text fontWeight="bold" fontSize="xs" color="gray.600">{title}</Text>
      {codes && codes.length > 0 ? (
        <Flex flexWrap="wrap" gap={1} mt={1}>
          {codes.map((code, idx) => (
            <Badge key={idx} colorScheme="blue" variant="subtle" fontSize="xs">{code}</Badge>
          ))}
        </Flex>
      ) : (
        <Text fontSize="xs" color="gray.400">None</Text>
      )}
    </Box>
  );

  return (
    <Box minH="100vh" bg="gray.50">
      <HeaderMenus />
      <Box p={4}>
        <Flex justify="space-between" align="center" mb={4}>
          <Box>
            <Heading size="md" mb={1}>Case Impact Portfolio</Heading>
            <Text color="gray.600" fontSize="sm">Dense view of clinical and financial variance across cases.</Text>
          </Box>
          <Button colorScheme="blue" size="sm" onClick={fetchPortfolio} isLoading={loading}>
            Refresh
          </Button>
        </Flex>

        <Box bg="white" shadow="sm" rounded="lg" overflow="hidden" borderWidth="1px">
          {loading ? (
            <Flex justify="center" align="center" p={10}>
              <Spinner size="xl" color="blue.500" />
            </Flex>
          ) : (
            <Box overflowX="auto" maxH="calc(100vh - 180px)" overflowY="auto">
              <Table variant="simple" size="sm">
                <Thead bg="gray.100" position="sticky" top={0} zIndex={1}>
                  <Tr>
                    <Th cursor="pointer" onClick={() => handleSort('impact_score')} whiteSpace="nowrap">
                      Score {sortConfig.key === 'impact_score' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                    </Th>
                    <Th cursor="pointer" onClick={() => handleSort('case_id')} whiteSpace="nowrap">
                      Case ID / Type {sortConfig.key === 'case_id' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                    </Th>
                    <Th minW="120px">Payer</Th>
                    <Th cursor="pointer" onClick={() => handleSort('financial_variance')} whiteSpace="nowrap">
                      Variance {sortConfig.key === 'financial_variance' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                    </Th>
                    <Th cursor="pointer" onClick={() => handleSort('variance_category')} minW="150px">
                      Category {sortConfig.key === 'variance_category' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                    </Th>
                    <Th minW="250px">Executive Summary</Th>
                    <Th minW="250px">Root Cause</Th>
                    <Th minW="250px">Revedy Solution</Th>
                    <Th minW="200px">Prospect Codes</Th>
                    <Th minW="200px">Revedy Codes</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {sortedData.map((row) => (
                    <Tr key={row.id} _hover={{ bg: 'gray.50' }}>
                      <Td verticalAlign="top">
                        <Badge colorScheme={getScoreColor(row.impact_score)} fontSize="sm" px={2} py={1} rounded="md">
                          {row.impact_score || 'N/A'}
                        </Badge>
                      </Td>
                      <Td verticalAlign="top" whiteSpace="nowrap">
                        <Text fontWeight="bold" fontSize="sm">
                          <a href={`/analyze-case-mn/${row.case_id}?claim_type=${row.claim_type}`} target="_blank" rel="noopener noreferrer" style={{ color: '#3182ce', textDecoration: 'underline' }}>
                            {row.case_id}
                          </a>
                        </Text>
                        <Badge mt={1} colorScheme={row.claim_type === 'pro' ? 'purple' : 'teal'} fontSize="xs">
                          {row.claim_type?.toUpperCase()}
                        </Badge>
                      </Td>
                      <Td verticalAlign="top" whiteSpace="normal">
                        <Text fontSize="xs" fontWeight="medium" color="gray.700">
                          {row.analysis_data?.payer_name || 'Unknown'}
                        </Text>
                      </Td>
                      <Td verticalAlign="top" whiteSpace="nowrap">
                        <Text fontWeight="bold" fontSize="sm" color={row.financial_variance > 0 ? 'green.600' : 'gray.700'}>
                          {formatCurrency(row.financial_variance)}
                        </Text>
                      </Td>
                      <Td verticalAlign="top">
                        <Text fontSize="xs" fontWeight="bold" color="gray.700" whiteSpace="normal">
                          {row.analysis_data?.variance_category || 'N/A'}
                        </Text>
                      </Td>
                      <Td verticalAlign="top" whiteSpace="normal">
                        <Text fontSize="xs" color="gray.800">
                          {row.analysis_data?.executive_summary || 'N/A'}
                        </Text>
                      </Td>
                      <Td verticalAlign="top" whiteSpace="normal">
                        <Text fontSize="xs" color="red.700">
                          {row.analysis_data?.root_cause_analysis || 'N/A'}
                        </Text>
                      </Td>
                      <Td verticalAlign="top" whiteSpace="normal">
                        <Text fontSize="xs" color="green.700">
                          {row.analysis_data?.revedy_solution || 'N/A'}
                        </Text>
                      </Td>
                      <Td verticalAlign="top" bg="gray.50">
                        {renderCodeList(row.analysis_data?.prospect_submitted_cpt, "CPT")}
                        {renderCodeList(row.analysis_data?.prospect_submitted_icd10, "ICD-10")}
                      </Td>
                      <Td verticalAlign="top" bg="green.50">
                        {renderCodeList(row.analysis_data?.revedy_generated_cpt, "CPT")}
                        {renderCodeList(row.analysis_data?.revedy_generated_icd10, "ICD-10")}
                      </Td>
                    </Tr>
                  ))}
                  {sortedData.length === 0 && (
                    <Tr>
                      <Td colSpan={9} textAlign="center" py={8} color="gray.500">
                        No impact analyses found.
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
